import { useLanguage, useTranslations } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import { useCallback, useEffect, useState } from 'react'
import { useClient, useLoading, useQuery } from '@based/sdk/react'
import { DiscoveryCrawlerPaginationFooter } from '@app/components/blocks/searchGrid/DiscoveryCrawlerPaginationFooter'
import {
  useDiscoveryCrawlerPage,
  usePageIndex,
} from '@app/util/hooks/useDiscoveryCrawlerPage'
import { MunicipalitySearchGrid } from '../sections/MunicipalitySearchGrid'
import Column from '@app/components/layout/Column'
import { ProvinceSection } from './sections/ProvinceSection'
import { FeaturedSection } from './sections/FeaturedSection'
import { ListMunicipalityModel } from 'functions/app/geography/list/municipality'
import { Empty } from '@app/components/blocks/empty/Empty'
import FindLocationImage from '@app/assets/images/find-location.png'

const EmptyDiscoverRegions = () => {
  const t = useTranslations().app.discoveryPage.tabs.regions
  return (
    <Empty
      title={t.emptyTitle}
      imageSrc={FindLocationImage}
      style={{ marginTop: '4rem' }}
    >
      {t.emptyDescription}
    </Empty>
  )
}

export const DiscoverRegions = ({
  searchTerm,
  onResetSearch,
}: {
  searchTerm: string
  onResetSearch: () => void
}) => {
  const PAGE_SIZE = 15
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.discoveryPage.tabs.regions
  const requestedPage = usePageIndex()
  const fetchTop = requestedPage * PAGE_SIZE
  const [total, setTotal] = useState(0)
  const [municipalities, setMunicipalities] = useState<ListMunicipalityModel[]>(
    [],
  )
  const [top, setTop] = useState(fetchTop)
  const [mounted, setMounted] = useState(false)
  const client = useClient()
  const isLoading = useLoading()
  const showSearchResults = searchTerm.trim() !== ''

  const dataServerQuery = useQuery('list-municipality', {
    searchTerm: searchTerm,
    top: fetchTop,
  })
  const serverMunicipalities: ListMunicipalityModel[] =
    dataServerQuery.data ?? []

  const renderedMunicipalities = mounted ? municipalities : serverMunicipalities
  const hasMore =
    renderedMunicipalities.length % PAGE_SIZE === 0 &&
    renderedMunicipalities.length <= fetchTop

  const { nextPageHref } = useDiscoveryCrawlerPage({
    hasMore: serverMunicipalities.length >= fetchTop,
  })

  const query = useCallback(
    (topParam: number) => {
      const close = client
        .query('list-municipality', { searchTerm: searchTerm, top: topParam })
        .subscribe((result) => {
          setMunicipalities(result)
          setMounted(true)
        })
      return () => close()
    },
    [client, searchTerm],
  )
  const countQuery = useCallback(() => {
    const close = client
      .query('list-municipality', { searchTerm: searchTerm, count: true })
      .subscribe((r) => setTotal(r.count))
    return () => close()
  }, [client, searchTerm])

  useEffect(() => {
    setTop(fetchTop)
    const cleanupCount = countQuery()
    const cleanupQuery = query(fetchTop)
    return () => {
      cleanupCount()
      cleanupQuery()
    }
  }, [searchTerm, fetchTop, countQuery, query])

  useEffect(() => {
    if (top === fetchTop) {
      return
    }
    const cleanup = query(top)
    return () => cleanup()
  }, [top, query, fetchTop])

  return (
    <Column gap="4rem" alignItems="stretch">
      <ProvinceSection searchTerm={searchTerm} onResetSearch={onResetSearch} />

      {!showSearchResults && <FeaturedSection />}

      <>
        <MunicipalitySearchGrid
          items={renderedMunicipalities.map((municipality) => ({
            id: municipality.id,
            slug: municipality.slug,
            avatar: municipality.image?.src,
            title: municipality.name,
            description: municipality.localities.map((l) => l.name)?.join(', '),
            hasBadge: municipality.crowdbuildingPartner,
            isLocked:
              !municipality.crowdbuildingPartner &&
              !municipality.region.crowdbuildingPartner,
            plots: municipality.plots?.count ?? 0,
            initiatives: municipality.initiatives.count,
            avatars: municipality.followers ?? [],
          }))}
          totalItems={total}
          title={translations.municipalitesTitle}
          subtitle={translations.municipalitesSubtitle}
          isLoading={isLoading}
          searchTerm={searchTerm}
          loadMore={() => setTop((prev) => prev + PAGE_SIZE)}
          onResetSearch={onResetSearch}
          hasMore={hasMore}
          footer={
            <DiscoveryCrawlerPaginationFooter nextPageHref={nextPageHref} />
          }
        />
        {renderedMunicipalities.length === 0 && !isLoading && (
          <EmptyDiscoverRegions />
        )}
      </>
    </Column>
  )
}
